import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { Icons } from "../lib/Icons";
import { saveDocumentExtractionCorrectionsV2 } from "../lib/api";

function splitLines(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map((v) => String(v).trim()).filter(Boolean);
  if (typeof value === "string") {
    if (!value.trim() || value === "No outcomes provided" || value === "No provision provided") return [];
    return value
      .split(/\r?\n/)
      .map((s) => s.replace(/^([•\-*–]|\d+[.)])\s+/, "").trim())
      .filter(Boolean);
  }
  return [];
}

function joinLines(value) {
  return splitLines(value).join("\n");
}

function mapSectionE(sectionE = {}) {
  return {
    communicationAndInteraction: joinLines(sectionE.CommunicationInteraction || sectionE.communicationAndInteraction),
    cognitionAndLearning: joinLines(sectionE.CognitionLearning || sectionE.cognitionAndLearning),
    semh: joinLines(sectionE.SEMH || sectionE.socialEmotionalMentalHealth || sectionE.semh),
    sensoryAndPhysical: joinLines(sectionE.SensoryPhysical || sectionE.sensoryAndPhysical || sectionE.sensoryAndOrPhysical),
  };
}

function mapSectionF(sectionF = {}) {
  return {
    educationalProvision: joinLines(sectionF.EducationalProvision),
    staffingAndFrequency: joinLines(sectionF.StaffingAndFrequency),
    specialistInput: joinLines(sectionF.SpecialistInput),
    environmentAndResources: joinLines(sectionF.EnvironmentAndResources),
  };
}

function buildSectionEPayload(sectionEText) {
  return {
    CommunicationInteraction: splitLines(sectionEText.communicationAndInteraction),
    CognitionLearning: splitLines(sectionEText.cognitionAndLearning),
    SEMH: splitLines(sectionEText.semh),
    SensoryPhysical: splitLines(sectionEText.sensoryAndPhysical),
  };
}

function buildSectionFPayload(sectionFText) {
  return {
    EducationalProvision: splitLines(sectionFText.educationalProvision),
    StaffingAndFrequency: splitLines(sectionFText.staffingAndFrequency),
    SpecialistInput: splitLines(sectionFText.specialistInput),
    EnvironmentAndResources: splitLines(sectionFText.environmentAndResources),
  };
}

function hasExtractionContent(sectionE = {}, sectionF = {}) {
  const mappedE = mapSectionE(sectionE);
  const mappedF = mapSectionF(sectionF);
  return Object.values(mappedE).some(Boolean) || Object.values(mappedF).some(Boolean);
}

function badgeClass(status) {
  if (status === "completed") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (status === "processing" || status === "queued") return "bg-blue-50 text-blue-700 border-blue-200";
  if (status === "failed") return "bg-red-50 text-red-700 border-red-200";
  return "bg-gray-50 text-gray-700 border-gray-200";
}

export default function EHCPIngestionTimeline({ studentId, schoolId }) {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [loadingExtraction, setLoadingExtraction] = useState(false);
  const [sourcePath, setSourcePath] = useState("");
  const [sectionEText, setSectionEText] = useState({
    communicationAndInteraction: "",
    cognitionAndLearning: "",
    semh: "",
    sensoryAndPhysical: "",
  });
  const [sectionFText, setSectionFText] = useState({
    educationalProvision: "",
    staffingAndFrequency: "",
    specialistInput: "",
    environmentAndResources: "",
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [hasCorrectionContent, setHasCorrectionContent] = useState(false);

  const selectedJob = useMemo(
    () => jobs.find((job) => job.jobId === selectedJobId) || null,
    [jobs, selectedJobId]
  );

  const refreshJobs = async () => {
    if (!studentId || !schoolId) return;
    setLoadingJobs(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, "v2_document_jobs"),
          where("schoolId", "==", schoolId),
          where("studentId", "==", studentId)
        )
      );
      const list = snap.docs
        .map((d) => ({ ...(d.data() || {}), jobId: d.id }))
        .filter((job) => job.jobType === 'extract_ehcp')
        .sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
      setJobs(list);
      if (!selectedJobId && list.length) {
        setSelectedJobId(list[0].jobId);
      } else if (selectedJobId && !list.some((j) => j.jobId === selectedJobId) && list.length) {
        setSelectedJobId(list[0].jobId);
      }
    } catch (error) {
      console.error("Failed to load ingestion jobs:", error);
      setMessage("Could not load ingestion timeline.");
    } finally {
      setLoadingJobs(false);
    }
  };

  useEffect(() => {
    refreshJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, schoolId]);

  useEffect(() => {
    const loadExtraction = async () => {
      if (!selectedJobId) return;
      setLoadingExtraction(true);
      setMessage("");
      try {
        const extractionSnap = await getDoc(doc(db, "v2_document_extractions", selectedJobId));
        const extraction = extractionSnap.exists() ? extractionSnap.data() : {};
        setSourcePath(extraction?.sourcePath || selectedJob?.filePath || "");
        setSectionEText(mapSectionE(extraction?.sectionE || {}));
        setSectionFText(mapSectionF(extraction?.sectionF || {}));
        setHasCorrectionContent(hasExtractionContent(extraction?.sectionE, extraction?.sectionF));
      } catch (error) {
        console.error("Failed to load extraction:", error);
        setMessage("Could not load extracted sections for this job.");
      } finally {
        setLoadingExtraction(false);
      }
    };
    loadExtraction();
  }, [selectedJobId, selectedJob?.filePath]);

  const saveCorrections = async () => {
    if (!selectedJobId) return;
    setSaving(true);
    setMessage("");
    try {
      await saveDocumentExtractionCorrectionsV2({
        jobId: selectedJobId,
        studentId,
        sourcePath,
        sectionE: buildSectionEPayload(sectionEText),
        sectionF: buildSectionFPayload(sectionFText),
      });
      setMessage("Corrections saved and student profile re-synthesized.");
      await refreshJobs();
    } catch (error) {
      console.error("Failed to save corrections:", error);
      setMessage(error?.message || "Failed to save corrections.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800 flex items-center">
          <Icons.Clock className="w-4 h-4 mr-2" />
          Ingestion Timeline & Manual Corrections
        </h3>
        <button
          onClick={refreshJobs}
          className="px-3 py-1 text-xs rounded bg-white border border-slate-200 hover:bg-slate-100"
        >
          Refresh
        </button>
      </div>

      {loadingJobs ? (
        <p className="text-sm text-slate-500">Loading document jobs...</p>
      ) : jobs.length === 0 ? (
        <p className="text-sm text-slate-500">No EHCP uploads yet. Upload an EHCP PDF to begin extraction.</p>
      ) : (
        <>
          <div className="mb-4 space-y-2">
            {jobs.slice(0, 6).map((job) => (
              <button
                key={job.jobId}
                onClick={() => setSelectedJobId(job.jobId)}
                className={`w-full rounded-md border p-2 text-left ${
                  selectedJobId === job.jobId ? "border-blue-300 bg-blue-50" : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-slate-800">{job.jobType || "document_job"}</div>
                  <span className={`text-xs px-2 py-0.5 rounded border ${badgeClass(job.status)}`}>
                    {job.status || "unknown"}
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {job.createdAt || "unknown time"} · {job.filePath || "no file path"}
                </div>
              </button>
            ))}
          </div>

          {loadingExtraction ? (
            <p className="text-sm text-slate-500">Loading extraction details...</p>
          ) : selectedJob?.status === 'failed' ? (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
              EHCP extraction failed for this upload. Try uploading the PDF again or contact support if the issue persists.
            </p>
          ) : hasCorrectionContent ? (
            <div className="space-y-4">
              <div className="rounded-md border border-slate-200 bg-white p-3">
                <p className="text-xs text-slate-600">
                  Review extracted Section E and F content, correct where needed, then save to update synthesis.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Section E - Communication & Interaction</label>
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm h-24 bg-white"
                    value={sectionEText.communicationAndInteraction}
                    onChange={(e) => setSectionEText((p) => ({ ...p, communicationAndInteraction: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Section E - Cognition & Learning</label>
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm h-24 bg-white"
                    value={sectionEText.cognitionAndLearning}
                    onChange={(e) => setSectionEText((p) => ({ ...p, cognitionAndLearning: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Section E - SEMH</label>
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm h-24 bg-white"
                    value={sectionEText.semh}
                    onChange={(e) => setSectionEText((p) => ({ ...p, semh: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Section E - Sensory & Physical</label>
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm h-24 bg-white"
                    value={sectionEText.sensoryAndPhysical}
                    onChange={(e) => setSectionEText((p) => ({ ...p, sensoryAndPhysical: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Section F - Educational Provision</label>
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm h-24 bg-white"
                    value={sectionFText.educationalProvision}
                    onChange={(e) => setSectionFText((p) => ({ ...p, educationalProvision: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Section F - Staffing & Frequency</label>
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm h-24 bg-white"
                    value={sectionFText.staffingAndFrequency}
                    onChange={(e) => setSectionFText((p) => ({ ...p, staffingAndFrequency: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Section F - Specialist Input</label>
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm h-24 bg-white"
                    value={sectionFText.specialistInput}
                    onChange={(e) => setSectionFText((p) => ({ ...p, specialistInput: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Section F - Environment & Resources</label>
                  <textarea
                    className="mt-1 w-full border rounded p-2 text-sm h-24 bg-white"
                    value={sectionFText.environmentAndResources}
                    onChange={(e) => setSectionFText((p) => ({ ...p, environmentAndResources: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={saveCorrections}
                  disabled={!selectedJobId || saving}
                  className="px-3 py-1.5 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save corrections and update profile"}
                </button>
              </div>
            </div>
          ) : selectedJob ? (
            <p className="text-sm text-slate-500">
              {selectedJob.status === 'completed'
                ? 'No Section E or F content was extracted from this upload.'
                : 'Extraction is still in progress. Refresh shortly to review extracted content.'}
            </p>
          ) : null}
        </>
      )}

      {message && (
        <div className="mt-3 text-sm text-slate-700 bg-white border border-slate-200 rounded p-2">
          {message}
        </div>
      )}
    </div>
  );
}
